import type {
  MessageActionRequest,
  SpecialUse,
  ThreadRow,
} from '@email/shared';
import {
  Archive,
  Bookmark,
  CheckSquare2,
  Clock3,
  Inbox,
  Loader2,
  Mail,
  MailOpen,
  ShieldAlert,
  ShieldCheck,
  Square,
  Star,
  Trash2,
  X,
} from 'lucide-react';

interface TrashToolbarState {
  count: number;
  pending: boolean;
  onEmpty: () => void;
}

export function MessageToolbar({
  selectedRows,
  allLoadedSelected,
  canSelectAll,
  currentSpecialUse,
  pending,
  onToggleAll,
  onClear,
  onAction,
  onMoveToInbox,
  onSnooze,
  onUnsnooze,
  snoozedView = false,
  trash,
  matchingSelection = false,
  matchingAllStarred = false,
  savedSearch,
}: {
  selectedRows: ThreadRow[];
  allLoadedSelected: boolean;
  canSelectAll: boolean;
  currentSpecialUse: SpecialUse | null;
  pending: boolean;
  onToggleAll: () => void;
  onClear: () => void;
  onAction: (action: MessageActionRequest['action']) => void;
  onMoveToInbox?: (() => void) | undefined;
  onSnooze: () => void;
  onUnsnooze: () => void;
  snoozedView?: boolean;
  trash?: TrashToolbarState | undefined;
  matchingSelection?: boolean;
  matchingAllStarred?: boolean;
  savedSearch?: {
    saved: boolean;
    onManage: () => void;
  };
}) {
  const hasSelection = matchingSelection || selectedRows.length > 0;
  const allStarred = matchingSelection
    ? matchingAllStarred
    : hasSelection && selectedRows.every((row) => row.starred);
  const actionsDisabled = pending || !hasSelection;
  const canReportSpam =
    currentSpecialUse !== 'trash' &&
    currentSpecialUse !== 'sent' &&
    currentSpecialUse !== 'drafts';
  const buttonClass =
    'rounded-full p-2 text-ink-muted hover:bg-hover hover:text-ink disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink-muted';

  return (
    <div className="flex h-12 shrink-0 items-center gap-1 overflow-x-auto border-b border-line bg-surface px-3">
      <button
        type="button"
        onClick={onToggleAll}
        disabled={!canSelectAll}
        className={buttonClass}
        title={matchingSelection || allLoadedSelected ? 'Clear selection' : 'Select all loaded messages'}
        aria-label={matchingSelection || allLoadedSelected ? 'Clear selection' : 'Select all loaded messages'}
      >
        {matchingSelection || allLoadedSelected ? (
          <CheckSquare2 className="size-4 text-accent" />
        ) : (
          <Square className="size-4" />
        )}
      </button>
      <span
        className="mr-2 min-w-20 shrink-0 whitespace-nowrap text-sm font-medium"
        aria-live="polite"
      >
        {matchingSelection
          ? 'Up to 500 matching'
          : `${selectedRows.length.toLocaleString()} selected`}
      </span>
      {savedSearch ? (
        <button
          type="button"
          onClick={savedSearch.onManage}
          className={buttonClass}
          title={savedSearch.saved ? 'Manage this saved search' : 'Save this search'}
          aria-label={savedSearch.saved ? 'Manage this saved search' : 'Save this search'}
        >
          <Bookmark
            className={`size-4 ${savedSearch.saved ? 'fill-accent text-accent' : ''}`}
          />
        </button>
      ) : null}
      <button
        type="button"
        onClick={() => onAction('read')}
        disabled={actionsDisabled}
        className={buttonClass}
        title="Mark read (Shift+I)"
        aria-label="Mark selected messages read"
      >
        <MailOpen className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => onAction('unread')}
        disabled={actionsDisabled}
        className={buttonClass}
        title="Mark unread (Shift+U)"
        aria-label="Mark selected messages unread"
      >
        <Mail className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => onAction(allStarred ? 'unstar' : 'star')}
        disabled={actionsDisabled}
        className={buttonClass}
        title={allStarred ? 'Remove star (s)' : 'Star (s)'}
        aria-label={allStarred ? 'Remove star from selected messages' : 'Star selected messages'}
      >
        <Star className={`size-4 ${allStarred ? 'fill-amber-400 text-amber-400' : ''}`} />
      </button>
      {currentSpecialUse === 'inbox' || snoozedView ? (
        <button
          type="button"
          onClick={snoozedView ? onUnsnooze : onSnooze}
          disabled={actionsDisabled}
          className={buttonClass}
          title={snoozedView ? 'Return to inbox now' : 'Snooze'}
          aria-label={snoozedView ? 'Return selected conversations to inbox now' : 'Snooze selected conversations'}
        >
          <Clock3 className="size-4" />
        </button>
      ) : null}
      {currentSpecialUse !== 'archive' && currentSpecialUse !== 'all' ? (
        <button
          type="button"
          onClick={() => onAction('archive')}
          disabled={actionsDisabled}
          className={buttonClass}
          title="Archive (e)"
          aria-label="Archive selected messages"
        >
          <Archive className="size-4" />
        </button>
      ) : null}
      {currentSpecialUse === 'trash' ? (
        <button
          type="button"
          onClick={onMoveToInbox}
          disabled={actionsDisabled || !onMoveToInbox}
          className={buttonClass}
          title={onMoveToInbox ? 'Move to Inbox' : 'This account does not have an Inbox folder'}
          aria-label="Move selected messages to Inbox"
        >
          <Inbox className="size-4" />
        </button>
      ) : null}
      {currentSpecialUse === 'junk' ? (
        <button
          type="button"
          onClick={() => onAction('not_spam')}
          disabled={actionsDisabled}
          className={buttonClass}
          title="Not spam"
          aria-label="Mark selected messages as not spam"
        >
          <ShieldCheck className="size-4" />
        </button>
      ) : canReportSpam ? (
        <button
          type="button"
          onClick={() => onAction('spam')}
          disabled={actionsDisabled}
          className={buttonClass}
          title="Report spam"
          aria-label="Report selected messages as spam"
        >
          <ShieldAlert className="size-4" />
        </button>
      ) : null}
      {currentSpecialUse !== 'trash' ? (
        <button
          type="button"
          onClick={() => onAction('trash')}
          disabled={actionsDisabled}
          className={buttonClass}
          title="Move to Trash (#)"
          aria-label="Move selected messages to Trash"
        >
          <Trash2 className="size-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onAction('delete_perm')}
          disabled={actionsDisabled}
          className={`${buttonClass} text-danger`}
          title="Delete forever (#)"
          aria-label="Permanently delete selected messages"
        >
          <Trash2 className="size-4" />
        </button>
      )}
      <button
        type="button"
        onClick={onClear}
        disabled={!hasSelection}
        className={`${buttonClass} ${trash ? '' : 'ml-auto'}`}
        title="Clear selection (Esc)"
        aria-label="Clear selection"
      >
        <X className="size-4" />
      </button>
      {trash ? (
        <>
          <span className="ml-auto hidden shrink-0 text-xs text-ink-muted sm:inline">
            {trash.count.toLocaleString()} in Trash
          </span>
          <button
            type="button"
            onClick={trash.onEmpty}
            disabled={trash.pending}
            className="flex shrink-0 items-center gap-2 rounded-full px-3 py-2 text-sm font-medium text-danger hover:bg-hover disabled:opacity-40"
          >
            {trash.pending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
            Empty Trash
          </button>
        </>
      ) : null}
    </div>
  );
}
